// ----------------------------------------------------------------------
// <copyright file="Izenda.TableUIStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

import fastdom from 'fastdom';
import { asReference, observable, computed } from 'mobx';
import {
  forEach,
  map,
  flow,
  max,
  isNil,
  sum,
  last,
  head,
  sortBy,
  slice,
  isEmpty,
  compact,
  pick,
  get,
  add,
  identity,
  filter,
  groupBy,
  toPairs,
  placeholder,
  flattenDepth,
  isUndefined
} from 'lodash/fp';

const parseFloatCustom = value => parseFloat(value) || 0;

const clamp = (num, min, max) => Math.min(Math.max(num, min), max);

const getHeight = (dom, withBorder) => {
  const domStyle = window.getComputedStyle(dom);

  return (
    parseFloatCustom(domStyle.height) -
    (!withBorder ? parseFloatCustom(domStyle.borderTop) + parseFloatCustom(domStyle.borderBottom) : 0)
  );
};

const setHeight = (value, dom) => {
  dom && (dom.style.height = value && `${value}px`);
};

const syncHeight = elements => {
  fastdom.mutate(() => {
    forEach(element => {
      setHeight(null, element);
    }, elements);

    fastdom.measure(() => {
      const heightMax = flow(
        map(element => (!element ? 0 : getHeight(element, true))),
        max
      )(elements);

      fastdom.mutate(() => {
        forEach(setHeight.bind(null, heightMax), elements);
      });
    });
  });
};

export default class TableUIStore {
  @observable tableHeight = 0;
  @observable headerHeight = 37;
  @observable virtualStore = asReference(null);
  @observable selectCell;
  @observable menuSetting;

  @computed
  get bodyHeight() {
    return Math.max(0, this.tableHeight - this.headerHeight);
  }

  constructor(defaultColumnWidth, defaultHeight, autoResize, onTableWidthChanged) {
    this.headerGroupElements = {};
    this.headerGroups = {};
    this.headerElements = [];
    this.headerElementsPivot = {};
    this.cellElements = [];
    this.columnWidths = [];
    this.columnActualWidths = [];
    this.defaultColumnWidth = defaultColumnWidth;
    this.border = 1;
    this.autoResize = autoResize;
    this.maxDeep = 0;
    this.onTableWidthChanged = onTableWidthChanged;

    this.tableHeight = defaultHeight;
    this.selectCell = {
      rowIndex: -1,
      columnIndex: -1,
      top: -1,
      left: -1,
      width: 0,
      height: 0,
      tableBody: {

      }
    };
    this.menuSetting = {
      isVisible: false,
      mouseEvent: {},
      sourceFlag: false,
      cellInfor: {
        field: {}
      }
    }
  }

  /**
   * Register header group cell element
   */
  registerHeaderCell(cellElem, headerColIndex, deep) {
    this.headerElements[headerColIndex] = cellElem;
    deep = isNil(deep) ? -1 : deep;
    this.maxDeep = max([this.maxDeep, deep]);

    if (!this.headerElementsPivot[deep]) {
      this.headerElementsPivot[deep] = [];
    }

    this.headerElementsPivot[deep][headerColIndex] = cellElem;
  }

  /**
   * Unregister header group cell element
   */
  unregisterHeaderCell(_cellElem, headerColIndex, deep) {
    delete this.headerElements[headerColIndex];
    deep = isNil(deep) ? -1 : deep;

    if (this.headerElementsPivot[deep]) {
      delete this.headerElementsPivot[deep][headerColIndex];
      if (isEmpty(compact(this.headerElementsPivot[deep]))) {
        delete this.headerElementsPivot[deep];
      }
    }
  }

  /**
   * Register header group cell element
   */
  registerHeaderGroupCell(cellElem, headerColIndex) {
    this.headerGroupElements[headerColIndex] = cellElem;
  }

  /**
   * Unregister header group cell element
   */
  unregisterHeaderGroupCell(_cellElem, headerColIndex) {
    delete this.headerGroupElements[headerColIndex];
  }

  /**
   * Register row cell element
   */
  registerCell(cellElem, columnIndex) {
    !this.cellElements[columnIndex] && (this.cellElements[columnIndex] = []);
    this.cellElements[columnIndex].push(cellElem);
  }

  /**
   * Unregister row cell element
   */
  unregisterCell(cellElem, columnIndex) {
    let cellIndex;
    const cells = this.cellElements[columnIndex];
    if (cells) {
      cellIndex = cells.indexOf(cellElem);
      cellIndex > -1 && cells.splice(cellIndex, 1);
    }
  }

  /**
   * Add header group info
   */
  addHeaderGroup(headerGroup) {
    this.headerGroups[headerGroup.ref] = headerGroup;
  }

  /**
   * Clean header groups
   */
  resetHeaderGroups() {
    this.headerGroups = {};
  }

  calculateHeaderGroupWidth(headeGroup) {
    return flow(slice(headeGroup.startColumn, headeGroup.endColumn), sum)(this.getActualColumnWidths());
  }

  updateHeaderHeight() {
    syncHeight(this.headerElementsPivot[this.maxDeep]);
  }

  updateCellsHeight(){
    const cells = flattenDepth(1, this.cellElements);
    syncHeight(cells);
  }

  getRowSpanHeight(startIndex, size) {
    const end = size && startIndex + size;

    return !this.virtualStore ? 0 : flow(map('height'), sum)(this.virtualStore.items.slice(startIndex, end));
  }

  updateHeaderGroupHeight() {
    fastdom.measure(() => {
      flow(
        map(identity),
        groupBy('deep'),
        toPairs,
        sortBy(head),
        map(last),
        forEach(headerGroups => {
          const keyList = map('ref', headerGroups);
          const headerGroupDoms = flow(pick(keyList), map(identity))(this.headerGroupElements);

          syncHeight(headerGroupDoms);
        })
      )(this.headerGroups);
    });
  }

  updateHeight(tableHeight, headerHeight) {
    this.tableHeight = tableHeight;
    if (!isUndefined(headerHeight))
      this.headerHeight = headerHeight;
  }

  updateSelectCell(cellInfor) {
    this.selectCell = cellInfor;
  }
  updateMenuSetting(menuSetting) {
    this.menuSetting = menuSetting;
  }

  updateSelectCell(cellInfor) {
    this.selectCell = cellInfor;
  }

  updateMenuSetting(menuSetting) {
    this.menuSetting = menuSetting;
  }

  registerVirtualStore(virtualStore) {
    this.virtualStore = virtualStore;
  }

  updateWidths(containerWidth, colsWidths, needUpdateColumn) {
    let adjustedWidths;
    const defaultColumnWidth = this.defaultColumnWidth;

    const fixWidth =
      placeholder(colsWidths)
        .filter('fixed')
        .map(get('width'))
        .reduce(add) || 0;

    const dynWidth =
      placeholder(colsWidths)
        .filter({
          fixed: false
        })
        .map(get('width'))
        .reduce(add) || 0;

    if (!this.autoResize || containerWidth === Infinity || !containerWidth || dynWidth + fixWidth > containerWidth) {
      adjustedWidths = map(get('width'), colsWidths);
    } else {
      adjustedWidths = map(
        widthInfo =>
          widthInfo.fixed ? widthInfo.width : Math.round(widthInfo.width / dynWidth * (containerWidth - fixWidth)),
        colsWidths
      );
      adjustedWidths[adjustedWidths.length - 1] += containerWidth - sum(adjustedWidths);
    }

    adjustedWidths = adjustedWidths.map(width => width || defaultColumnWidth);

    // update column width (not actual)
    this.columnWidths = adjustedWidths;

    if (needUpdateColumn) {
      this.getActualColumnWidths().forEach((width, index) => {
        this.resizeColumn(index, width, false);
      });
    }
  }

  getColumnWidth(index) {
    return index < this.columnWidths.length
      ? (index < this.columnActualWidths.length && this.columnActualWidths[index])
        ? this.columnActualWidths[index]
        : this.columnWidths[index]
      : this.defaultColumnWidth;
  }

  getActualColumnWidths() {
    return this.columnWidths.map((_w,i) => this.getColumnWidth(i));
  }

  getTableWidth() {
    return sum(this.getActualColumnWidths());
  }

  resizeColumn(index, width, isUserAction) {
    let headerGroupWidth = 0,
      headerGroupElement,
      headerElement;

    if (isUserAction)
      this.columnActualWidths[index] = width;

    // calc table width
    const tableWidth = this.getTableWidth();
    this.onTableWidthChanged(tableWidth, index);

    fastdom.mutate(() => {
      forEach(cellElem => {
        $izenda(cellElem).css({
          width: `${width}px`,
          minWidth: `${width}px`,
          maxWidth: `${width}px`
        });
      }, this.cellElements[index]);

      flow(
        map(identity),
        filter(group => group.startColumn <= index && group.endColumn > index),
        forEach(headerGroup => {
          headerGroupWidth = this.calculateHeaderGroupWidth(headerGroup);
          headerGroupElement = this.headerGroupElements[headerGroup.ref];
          headerGroupElement &&
          $izenda(headerGroupElement).css({
            width: `${headerGroupWidth}px`,
            minWidth: `${headerGroupWidth}px`,
            maxWidth: `${headerGroupWidth}px`
          });
        })
      )(this.headerGroups);

      if (!isUserAction) {
        headerElement = this.headerElements[index];
        headerElement &&
        $izenda(headerElement).css({
          width: `${width}px`,
          minWidth: `${width}px`,
          maxWidth: `${width}px`
        });
      }
    });
  }

  /**
   * Update top position
   * @param {Element} currentNode
   * @param {Element} tableDom
   */
  updateTopPosition(currentNode, tableDom) {
    const tableHeight = tableDom.offsetHeight;
    const currentRect = currentNode.getBoundingClientRect();
    const listVirtualStickTop = $izenda(tableDom).find('.u-virtualStickTop');
    this.listStickTop = $izenda(tableDom).find('.u-stickTop');

    this.listStickTop.each((index, item) => {
      const cloneRect = listVirtualStickTop.get(index).getBoundingClientRect();
      const newPosition = clamp(currentRect.top - cloneRect.top, 0, tableHeight - item.offsetHeight);

      item.style.setProperty('z-index', 1);
      item.style.setProperty('top', `${newPosition}px`);
    });
  }

  /**
   * Reset top position
   */
  resetTopPosition() {
    if (this.listStickTop) {
      this.listStickTop.each((_index, item) => {
        item.style.removeProperty('z-index');
        item.style.removeProperty('top');
      });
      this.listStickTop = null;
    }
  }

  /**
   * Update left position
   * @param {Element} currentNode
   * @param {Element} tableDom
   */
  updateLeftPosition(currentNode, tableDom) {
    const tableWidth = this.getTableWidth();
    const currentRect = currentNode.getBoundingClientRect();
    const listVirtualStickLeft = $izenda(tableDom).find('.u-virtualStickLeft');
    this.listStickLeft = $izenda(tableDom).find('.u-stickLeft');

    this.listStickLeft.each((index, item) => {
      if ($izenda(item).is(':visible')) {
        const cloneRect = listVirtualStickLeft.get(index).getBoundingClientRect();
        const left = parseInt(listVirtualStickLeft.get(index).style.left || 0);
        const newPosition = clamp(currentRect.left - cloneRect.left + left, 0, tableWidth - item.offsetWidth);
        this.setLeftPosition(item, newPosition);
      }
    });
  }

  /**
   * Set left position
   * @param {[type]} element
   * @param {[type]} leftPosition
   */
  setLeftPosition(element, leftPosition) {
    if (element) {
      element.style.setProperty('z-index', 1);
      element.style.setProperty('position', 'relative');
      element.style.setProperty('left', `${leftPosition}px`);
    }
  }

  /**
   * Reset left position
   */
  resetLeftPosition() {
    if (this.listStickLeft) {
      this.listStickLeft.each((_index, item) => {
        item.style.removeProperty('z-index');
        item.style.removeProperty('position');
        item.style.removeProperty('left');
      });
    }
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/controls/DataGrid/Izenda.TableUIStore.js